import {useMutation} from '@apollo/client';
import {Button} from '@dabafinance/react-components';
import {Table, Tag} from 'antd';
import {currencySymbols} from 'utils/mock';
import {AUTOMATE_WITHDRAWAL} from 'graphql/mutations/wallet';
import UserLink from 'components/userLink/UserLink';
import {toast} from 'react-toastify';
import styles from './walletTransaction.module.scss';

export const BulkAutomateTransfer = ({
  rows,
  onClose,
  setSelectedRows,
  refetch,
}) => {
  const [automateWithdrawal, {loading}] = useMutation(AUTOMATE_WITHDRAWAL, {
    errorPolicy: 'all',
    onError: error => toast.error(error.message),
  });

  const handleAutomateTransfer = () => {
    automateWithdrawal({
      variables: {
        input: {
          id: '',
          currency: 'USD',
          withdrawals: rows.map(row => ({
            id: row.id,
            currency: row.currency,
          })),
        },
      },
    })
      .then(({data: {automateWithdrawal}}) => {
        if (automateWithdrawal.__typename === 'Error') {
          toast.error(automateWithdrawal.message);
        } else {
          refetch();
          toast.success('Automate Transfer Successfully');
          onClose();
          setSelectedRows();
        }
      })
      .catch(error => toast.error(error.message));
  };

  return (
    <div>
      <div className={styles.modalHeader}>
        <h1>Bulk Automate Transfer</h1>
        <p>
          Are you sure you want to automate the transfer of the selected rows?
        </p>
      </div>
      {rows.length > 0 && (
        <Table
          size="small"
          bordered
          rowKey="id"
          pagination={false}
          dataSource={rows}
          style={{marginBottom: 16}}
          columns={[
            {
              title: 'Name',
              dataIndex: 'user',
              key: 'name',
              render: (_, record) => (
                <span>
                  <UserLink userId={record?.user?.id} tab="wallet-settings">
                    {`${record?.user?.firstName || ''} ${
                      record?.user?.lastName || ''
                    }`.trim()}
                  </UserLink>
                </span>
              ),
            },
            {
              title: 'Currency',
              dataIndex: 'currency',
              key: 'currency',
            },
            {
              title: 'Payment Method',
              dataIndex: ['paymentMetadata', 'paymentMethod'],
              key: 'paymentMethod',
              render: (_, record) =>
                record?.paymentMetadata?.paymentMethod ? (
                  <Tag
                    color={
                      {
                        'WIRE_TRANSFER': 'gold',
                        'BANK_TRANSFER': 'geekblue',
                        'CRYPTO': 'orange',
                        'THIRD_PARTY_FLUTTERWAVE': 'purple',
                        'THIRD_PARTY_PAYSTACK': 'magenta',
                        'THIRD_PARTY_PAYPAL': 'cyan',
                        'THIRD_PARTY_SVB': 'volcano',
                        'THIRD_PARTY_REVOLUT': 'green',
                        'THIRD_PARTY_WISE': 'lime',
                        'MOBILE_MONEY': 'blue',
                        'HUB2_MOBILE_MONEY': 'geekblue',
                        'MAVIANCE_MOBILE_MONEY': 'orange',
                      }[record?.paymentMetadata?.paymentMethod] || 'default'
                    }>
                    {record?.paymentMetadata?.paymentMethod
                      ?.replace(/_/g, ' ')
                      ?.toLowerCase()
                      ?.replace(/\b\w/g, c => c.toUpperCase())}
                  </Tag>
                ) : (
                  '-- -- --'
                ),
            },
            {
              title: 'Total Amount',
              dataIndex: 'totalTransactionAmountIncludingFees',
              key: 'totalAmount',
              render: (_, record) => (
                <span>
                  {currencySymbols[record?.currency]?.symbol}
                  {Number(
                    record?.totalTransactionAmountIncludingFees || 0,
                  )?.toLocaleString()}
                </span>
              ),
            },
            {
              title: 'Amount (Net of Fees)',
              dataIndex: 'transactionAmountNetOfFees',
              key: 'netAmount',
              render: (_, record) => (
                <span>
                  {currencySymbols[record?.currency]?.symbol}
                  {Number(
                    record?.transactionAmountNetOfFees || 0,
                  )?.toLocaleString()}
                </span>
              ),
            },
            {
              title: 'Fees',
              dataIndex: 'transactionFee',
              key: 'fees',
              render: (_, record) => (
                <span>
                  {currencySymbols[record?.currency]?.symbol}
                  {Number(record?.transactionFee || 0)?.toLocaleString()}
                </span>
              ),
            },
          ]}
        />
      )}
      <div className={styles.modalButtons}>
        <Button type="secondary" label="Cancel" onClick={onClose} />
        <Button
          label="Automate Transfer"
          onClick={handleAutomateTransfer}
          disabled={loading}
        />
      </div>
    </div>
  );
};
